import React, {
	createContext,
	useContext,
	useMemo,
} from 'react';
import { ConsumptionSetAttachedTo } from '@shared/utils/constants';
import { getDefaultConsumptionSetId } from '@shared/utils/getDefaultConsumptionSetId';
import type { GetEstimateDetailsResponse } from '@api/features/estimates/getEstimateDetails';
import type { Port } from '@api/utils/ports';
import type { ConsumptionSetProps } from '@api/models/consumption-set';
import { PerformanceEntryProps } from '@api/models/performance-entry';
import { SeaPassageSettings } from '@api/utils/sequelize/calculateVoyageItineraryDetails/helpers/itineraryConstants';
import { useMiscHandlers } from './hooks/useMiscHandlers';
import {
	LocalPortRotation,
	NewPortRotationEntryFormValues,
	usePortRotationHandlers,
} from './hooks/usePortRotationHandlers';
import { useCentralStore } from './CentralStoreContext';
import { useCargoProvider } from './CargoContext';

export type PortRotationContextType = {
	portRotation: (LocalPortRotation & { key: string })[];
	intakePortRotation: (LocalPortRotation & { key: string })[];
	onPortRotationChange: (entries: LocalPortRotation[]) => Promise<void> | void;
	onPortRotationRowChange: (
		id: number | undefined,
		attributes: LocalPortRotation
	) => Promise<void> | void;
	onOrderChange: (entries: LocalPortRotation[]) => Promise<void>;
	onConfigUpdate: (id: number, routeOptions: SeaPassageSettings) => Promise<void>;
	onDeletePortRotationEntry: (entryToDelete: LocalPortRotation) => void;
	onAddPortRotationEntry: (
		finalFormValues: NewPortRotationEntryFormValues,
		onAdd: (editing: boolean) => void
	) => void;
	estimateResult: GetEstimateDetailsResponse | null | undefined;
	ports: Port[] | undefined;
	portOptions: {
		label: string;
		value: number;
	}[];
	consumptionSets: Array<ConsumptionSetProps & { PerformanceEntries: Array<PerformanceEntryProps> }>;
	defaultConsumptionSetId: number | undefined;
	loading: boolean;
	pendingChanges: LocalPortRotation[];
	setPendingChanges: (changes: LocalPortRotation[]) => void;
	pendingPortRotationOrderChanges: LocalPortRotation[];
	setPendingPortRotationOrderChanges: (changes: LocalPortRotation[]) => void;
	setSyncPortRotation: React.Dispatch<React.SetStateAction<'full' | 'partial' | undefined>>;
};

export const PortRotationContext = createContext<PortRotationContextType>(undefined!);
export const usePortRotationProvider = () => useContext(PortRotationContext);

const PortRotationProvider = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	const {
		cargoReletConsumptionSet,
		hasCargoRelet,
	} = useCargoProvider();

	const {
		estimate,
		portRotation: centralPortRotation,
		syncPortRotation,
		setSyncPortRotation,
		vessel,
		selectedEstimateId,
		onUpdateFixture,
		loading,
	} = useCentralStore();

	const {
		portRotation,
		intakePortRotation,
		onPortRotationChange,
		onPortRotationRowChange,
		onOrderChange,
		onConfigUpdate,
		onAddPortRotationEntry,
		onDeletePortRotationEntry,
		pendingChanges,
		setPendingChanges,
		pendingPortRotationOrderChanges,
		setPendingPortRotationOrderChanges,
	} = usePortRotationHandlers({
		onUpdateFixture,
		estimate,
		syncPortRotation,
		setSyncPortRotation,
		centralPortRotation,
		selectedEstimateId,
	});

	const {
		ports,
		portOptions,
	} = useMiscHandlers();

	const consumptionSets = useMemo(() => {
		if (hasCargoRelet && cargoReletConsumptionSet != null) {
			return [cargoReletConsumptionSet];
		}

		return vessel?.consumptionSets.filter((cs) => (
			cs.attachedTo === ConsumptionSetAttachedTo.VESSEL ||
			cs.estimateId === estimate?.id
		)) ?? [];
	}, [
		hasCargoRelet,
		cargoReletConsumptionSet,
		vessel?.consumptionSets,
		estimate?.id,
	]);

	const defaultConsumptionSetId = useMemo(
		() => (hasCargoRelet ?
			consumptionSets.at(0)?.id :
			estimate?.vessel?.defaultConsumptionSetId ?? getDefaultConsumptionSetId(portRotation, consumptionSets)),
		[
			estimate?.vessel?.defaultConsumptionSetId,
			portRotation,
			consumptionSets,
			hasCargoRelet,
		],
	);

	return (
		<PortRotationContext.Provider
			value={{
				portRotation,
				intakePortRotation,
				onPortRotationChange,
				onPortRotationRowChange,
				onOrderChange,
				onConfigUpdate,
				onDeletePortRotationEntry,
				onAddPortRotationEntry,
				estimateResult: estimate,
				ports,
				portOptions,
				consumptionSets,
				defaultConsumptionSetId,
				loading,
				pendingChanges,
				setPendingChanges,
				pendingPortRotationOrderChanges,
				setPendingPortRotationOrderChanges,
				setSyncPortRotation,
			}}
		>
			{children}
		</PortRotationContext.Provider>
	);
};

export default PortRotationProvider;
